import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  PencilIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ProjectNavigation from '../components/projects/ProjectNavigation'
import VideoUploadModal from '../components/projects/VideoUploadModal'
import { useAuth } from '../contexts/AuthContext'
import {
  uploadFilesWithConcurrency,
  validateImageFiles,
  type UploadFailure,
  type UploadProgress,
} from '../features/image-upload/imageUpload'
import ImageUploadStatus from '../features/image-upload/ImageUploadStatus'
import { projectAPI, imageAPI, userProjectAPI } from '../services/api'
import { Project, ImageData, VideoStatus, UserProjectWithDetails } from '../types'

const IMAGE_UPLOAD_CONCURRENCY = 3

export default function ProjectEditPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [project, setProject] = useState<Project | null>(null)
  const [images, setImages] = useState<ImageData[]>([])
  const [completedUsers, setCompletedUsers] = useState<UserProjectWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imageUploadProgress, setImageUploadProgress] = useState<UploadProgress | null>(null)
  const [imageUploadFailures, setImageUploadFailures] = useState<UploadFailure[]>([])
  const [deletingImage, setDeletingImage] = useState<string | null>(null)
  const [editingName, setEditingName] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [taskCheckInterval, setTaskCheckInterval] = useState<NodeJS.Timeout | null>(null)
  const [showKeypointSchema, setShowKeypointSchema] = useState(false)
  const [showSkeletonSchema, setShowSkeletonSchema] = useState(false)

  // Redirect non-admin users
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/projects')
    }
  }, [user, navigate])

  useEffect(() => {
    if (projectId && user?.is_admin) {
      fetchProjectData()
    }

    return () => {
      if (taskCheckInterval) {
        clearInterval(taskCheckInterval)
      }
    }
  }, [projectId, user])

  useEffect(() => {
    // Check task status if processing
    if (project?.video_task_id && project.video_status === VideoStatus.PROCESSING) {
      const interval = setInterval(() => {
        checkTaskStatus(project.video_task_id!)
      }, 2000)
      setTaskCheckInterval(interval)
    } else if (taskCheckInterval) {
      clearInterval(taskCheckInterval)
      setTaskCheckInterval(null)
    }
  }, [project?.video_task_id, project?.video_status])

  // showLoading swaps the whole page for a spinner. Refreshes that run while the user
  // is looking at upload results keep the page mounted instead.
  const fetchProjectData = async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      const [projectRes, imagesRes, usersRes] = await Promise.all([
        projectAPI.get(projectId!),
        imageAPI.listAll(projectId!),
        userProjectAPI.getProjectUsers(projectId!),
      ])
      setProject(projectRes.data)
      setImages(imagesRes.data)

      // Filter users who marked project as completed (QA approved only)
      const completed = usersRes.data.filter(
        (up: UserProjectWithDetails) => up.status === 'qa_approved'
      )
      setCompletedUsers(completed)
    } catch (err) {
      console.error('Error fetching project:', err)
      alert('Failed to load project')
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  const checkTaskStatus = async (taskId: string) => {
    try {
      const response = await projectAPI.getTaskStatus(taskId)
      if (response.data.status === 'SUCCESS' || response.data.status === 'FAILURE') {
        fetchProjectData()
      }
    } catch (err) {
      console.error('Error checking task status:', err)
    }
  }

  const handleVideoUpload = async (file: File | null, params: any) => {
    if (!file) return

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('video', file)
      Object.keys(params).forEach(key => {
        // Convert boolean to string properly for FormData
        const value = params[key]
        formData.append(key, String(value))
      })

      await projectAPI.uploadVideo(projectId!, formData)
      setShowUploadModal(false)

      // Refresh data
      fetchProjectData()
    } catch (err) {
      console.error('Error uploading video:', err)
      alert('Failed to upload video')
    } finally {
      setUploading(false)
    }
  }

  const uploadImageFiles = async (files: File[], retainedFailures: UploadFailure[]) => {
    const total = retainedFailures.length + files.length
    setUploadingImage(true)
    setImageUploadFailures(retainedFailures)
    setImageUploadProgress({
      total,
      completed: retainedFailures.length,
      succeeded: 0,
      failed: retainedFailures.length,
    })
    try {
      const result = await uploadFilesWithConcurrency(
        files,
        file => imageAPI.upload(projectId!, file),
        IMAGE_UPLOAD_CONCURRENCY,
        progress => {
          setImageUploadProgress({
            total,
            completed: retainedFailures.length + progress.completed,
            succeeded: progress.succeeded,
            failed: retainedFailures.length + progress.failed,
          })
        }
      )
      setImageUploadFailures([...retainedFailures, ...result.failed])

      if (result.successful.length > 0) {
        await fetchProjectData({ showLoading: false })
      }
    } finally {
      setUploadingImage(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? [])
    // Clear the input up front. The browser skips change when the same files are picked
    // again while a value is still set, and the input may be gone by the time we finish.
    e.target.value = ''
    if (selectedFiles.length === 0) return

    const validation = validateImageFiles(selectedFiles)
    if (validation.validFiles.length === 0) {
      setImageUploadFailures(validation.failures)
      setImageUploadProgress({
        total: selectedFiles.length,
        completed: selectedFiles.length,
        succeeded: 0,
        failed: selectedFiles.length,
      })
      return
    }

    await uploadImageFiles(validation.validFiles, validation.failures)
  }

  const handleRetryFailedImages = async () => {
    const retryableFiles = imageUploadFailures
      .filter(failure => failure.retryable)
      .map(failure => failure.file)
    const validationFailures = imageUploadFailures.filter(failure => !failure.retryable)

    if (retryableFiles.length > 0) {
      await uploadImageFiles(retryableFiles, validationFailures)
    }
  }

  const handleDeleteImage = async (imageId: string) => {
    if (!confirm('Are you sure you want to delete this image? All annotations will be lost.'))
      return

    try {
      setDeletingImage(imageId)
      await imageAPI.delete(projectId!, imageId)
      await fetchProjectData()
    } catch (err: any) {
      console.error('Error deleting image:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to delete image'
      alert(errorMessage)
    } finally {
      setDeletingImage(null)
    }
  }

  const handleRenameProject = async () => {
    if (!newProjectName.trim() || newProjectName === project?.name) {
      setEditingName(false)
      setNewProjectName('')
      return
    }

    try {
      await projectAPI.update(projectId!, { name: newProjectName })
      await fetchProjectData()
      setEditingName(false)
      setNewProjectName('')
    } catch (err: any) {
      console.error('Error renaming project:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to rename project'
      alert(errorMessage)
    }
  }

  const handleDeleteProject = async () => {
    if (
      !confirm(
        `Are you sure you want to delete "${project?.name}"? This will delete all images, annotations, and cannot be undone.`
      )
    ) {
      return
    }

    try {
      await projectAPI.delete(projectId!)
      navigate('/projects')
    } catch (err: any) {
      console.error('Error deleting project:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to delete project'
      alert(errorMessage)
    }
  }

  const getVideoStatusIcon = () => {
    switch (project?.video_status) {
      case VideoStatus.NONE:
        return null
      case VideoStatus.UPLOADED:
        return <CloudArrowUpIcon className="w-5 h-5 text-blue-500" />
      case VideoStatus.PROCESSING:
        return <ArrowPathIcon className="w-5 h-5 text-yellow-500 animate-spin" />
      case VideoStatus.COMPLETED:
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case VideoStatus.FAILED:
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
      default:
        return null
    }
  }

  const getVideoStatusText = () => {
    switch (project?.video_status) {
      case VideoStatus.NONE:
        return 'No video uploaded'
      case VideoStatus.UPLOADED:
        return 'Video uploaded'
      case VideoStatus.PROCESSING:
        return 'Processing video...'
      case VideoStatus.COMPLETED:
        return `${project.video_frame_count || 0} frames extracted`
      case VideoStatus.FAILED:
        return 'Processing failed'
      default:
        return ''
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading project...</p>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Project not found</p>
        <button className="btn-primary" onClick={() => navigate('/projects')}>
          Back to Projects
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Projects
          </button>
          <ProjectNavigation projectId={projectId!} activePage="edit" />
        </div>

        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {editingName ? (
                <input
                  type="text"
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  onBlur={handleRenameProject}
                  onKeyPress={e => e.key === 'Enter' && handleRenameProject()}
                  className="text-3xl font-bold text-gray-900 border-b-2 border-blue-500 focus:outline-none"
                  autoFocus
                />
              ) : (
                <>
                  <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
                  <button
                    onClick={() => {
                      setEditingName(true)
                      setNewProjectName(project.name)
                    }}
                    className="text-gray-600 hover:text-gray-900"
                    title="Rename project"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            {project.description && <p className="text-gray-600 mt-2">{project.description}</p>}
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
              <span>{project.keypoint_schema.length} keypoints</span>
              <span>•</span>
              <span>{images.length} images</span>
              <span>•</span>
              <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 items-end">
            <div className="flex gap-3">
              {project.video_status === VideoStatus.NONE && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="btn-primary flex items-center"
                >
                  <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                  Upload Video
                </button>
              )}
              <button onClick={handleDeleteProject} className="btn-danger flex items-center">
                <TrashIcon className="w-5 h-5 mr-2" />
                Delete Project
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Status Section */}
      {project.video_status !== VideoStatus.NONE && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getVideoStatusIcon()}
              <div>
                <p className="font-medium text-gray-900">{getVideoStatusText()}</p>
                {project.video_error_message && (
                  <p className="text-sm text-red-600 mt-1">{project.video_error_message}</p>
                )}
                {project.video_uploaded_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    Uploaded {new Date(project.video_uploaded_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            {project.video_extraction_params && (
              <div className="text-sm text-gray-600">
                <p>Method: {project.video_extraction_params.extraction_method}</p>
                <p>Frames: {project.video_extraction_params.num_frames}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Who Completed */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <UserGroupIcon className="w-6 h-6" />
          Users Who Completed ({completedUsers.length})
        </h2>
        {completedUsers.length === 0 ? (
          <p className="text-gray-600">No users have marked this project as completed yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedUsers.map(userProject => (
              <div
                key={userProject.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <CheckCircleIconSolid className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">{userProject.user?.name}</p>
                  <p className="text-xs text-gray-600">Status: {userProject.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Keypoint Schema Display */}
      <div className="mb-6 bg-white rounded-lg shadow">
        <button
          className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={() => setShowKeypointSchema(!showKeypointSchema)}
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Keypoint Schema ({project.keypoint_schema.length} points)
          </h2>
          {showKeypointSchema ? (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
          )}
        </button>
        {showKeypointSchema && (
          <div className="px-6 pb-6 border-t">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-4">
              {project.keypoint_schema.map((keypoint, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">
                    {index}
                  </span>
                  <span className="text-sm text-gray-700">{keypoint}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Skeleton Schema Display */}
      {project.skeleton_schema.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow">
          <button
            className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            onClick={() => setShowSkeletonSchema(!showSkeletonSchema)}
          >
            <h2 className="text-xl font-semibold flex items-center gap-2">
              Skeleton Connections ({project.skeleton_schema.length} connections)
            </h2>
            {showSkeletonSchema ? (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRightIcon className="w-5 h-5 text-gray-500" />
            )}
          </button>
          {showSkeletonSchema && (
            <div className="px-6 pb-6 border-t">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-4">
                {project.skeleton_schema.map((connection, index) => (
                  <div key={index} className="text-sm text-gray-700">
                    {project.keypoint_schema[connection[0] - 1]} →{' '}
                    {project.keypoint_schema[connection[1] - 1]}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Images Management */}
      <ImageUploadStatus
        uploading={uploadingImage}
        progress={imageUploadProgress}
        failures={imageUploadFailures}
        onRetryFailed={handleRetryFailedImages}
      />

      {/* Kept outside the branches below so the picker survives the empty/non-empty switch */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        aria-label="Choose images to upload"
        accept=".png,.jpg,.jpeg,.bmp,.tiff"
        multiple
        onChange={handleImageUpload}
        disabled={uploadingImage}
      />

      {images.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <PhotoIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No images yet</h3>
          <p className="text-gray-600 mb-6">
            Upload a video to extract frames or upload images manually
          </p>
          <div className="flex gap-3 justify-center">
            {project.video_status === VideoStatus.NONE && (
              <button onClick={() => setShowUploadModal(true)} className="btn-primary">
                Upload Video
              </button>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className={`btn-secondary flex items-center ${
                uploadingImage ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}
            >
              <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
              Upload Images
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Project Images ({images.length})</h2>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className={`btn-secondary flex items-center text-sm ${
                uploadingImage ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}
            >
              <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
              Upload Images
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {images.map((image, index) => (
              <div key={image.id} className="relative group">
                <div className="bg-gray-200 rounded-lg overflow-hidden">
                  <img
                    src={image.url || ''}
                    alt={image.filename}
                    className="w-full h-auto"
                    style={{ maxHeight: '200px', objectFit: 'contain' }}
                    loading="lazy"
                    onError={e => {
                      const target = e.target as HTMLImageElement
                      if (!target.src.includes('data:image')) {
                        target.src =
                          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='
                      }
                    }}
                  />
                </div>

                <button
                  onClick={() => handleDeleteImage(image.id)}
                  disabled={deletingImage === image.id}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>

                <div className="mt-1">
                  <p className="text-xs text-gray-600 truncate">
                    {image.meta_data?.source === 'video_extraction'
                      ? `Frame ${image.frame_number || 0}`
                      : `Image ${index + 1}`}
                  </p>
                  {image.meta_data?.source === 'manual_upload' && (
                    <p className="text-xs text-gray-500">
                      {new Date(image.meta_data.uploaded_at!).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUploadModal && (
        <VideoUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleVideoUpload}
          uploading={uploading}
        />
      )}
    </div>
  )
}
